import { Button } from "..";
import { useCallback, useEffect, useState } from "react";
import "./create.scss";
import CreateOptions from "./CreateOptions";
import MediaDetails from "../../routes/pages/MediaDetail/MediaDetails";
import MediaPublish from "../../routes/pages/MediaPublish/MediaPublish";
import Modal from "../Modal/Modal";
import { MediaDetailConfig } from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import { Video, Search, X } from "lucide-react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { connect, ConnectedProps } from "react-redux";
import { callRegisterPosterAssetOnCms } from "@/store/upload/upload.action";
import { getUserBucketList } from "@/store/media/media.action";
import { getBunchListData } from "@/store/constants/constants.action";
import {
  emptyAiVoices,
  emptyAiLanguages,
  setIsEditPageChanged,
} from "@/store/media/media.slice";
import { updateModal } from "@/store/app/app.slice";
import { RootState, store } from "../../store";
import { resolveBucketEntries } from "./createBucketEntries";
import { BulkMediaItem } from "./BulkUploadForm";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";

interface IFileUploaderOwnProps {
  emptyAssetDetails: any;
  emptyMediaDetails: any;
  currentMediaDetailsForEdit: any;
  getMedia: any;
  updateIsClearFilter?: (params: any) => void;
  updateIsFilter?: (params: any) => void;
  bunchProductList?: any;
}

type Props = ConnectedProps<typeof connector> & IFileUploaderOwnProps;

const FileUploaderBase = ({
  emptyAssetDetails,
  emptyMediaDetails,
  currentMediaDetailsForEdit,
  getMedia,
  updateIsClearFilter,
  updateIsFilter,
  bunchProductList,
  callRegisterPosterAssetOnCms,
  createBucketOrder,
  defaultTeam,
  adminBucketList,
  getUserBucketList,
  getBunchListData,
  emptyAiVoices,
  emptyAiLanguages,
  updateModal,
  isEditPageChanged,
}: Props) => {
  const bucketCount = resolveBucketEntries(
    createBucketOrder,
    (defaultTeam as any)?.bucket_order,
    (adminBucketList ?? []).filter((b: any) => b.status === 1)
  ).length;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalOpenUpload, setIsModalOpenUpload] = useState(false);
  const [publishModal, setPublishModal] = useState(false);
  const [isBulkWizardOpen, setIsBulkWizardOpen] = useState(false);
  const [bulkMediaItems, setBulkMediaItems] = useState<BulkMediaItem[]>([]);
  const [activeBulkTab, setActiveBulkTab] = useState(0);
  const [bulkActiveView, setBulkActiveView] = useState<"edit" | "publish">(
    "edit"
  );
  // Guards switching/closing a bulk-upload tab while it has unsaved edits -
  // pendingBulkTabAction holds the switch/close to run once the user confirms
  // discarding those changes in the AlertDialog below.
  const [showBulkTabAlert, setShowBulkTabAlert] = useState(false);
  const [pendingBulkTabAction, setPendingBulkTabAction] = useState<
    (() => void) | null
  >(null);
  const [ttsNextDisabled, setTtsNextDisabled] = useState(true);
  const [ttsRpcPending, setTtsRpcPending] = useState(false);
  const [premiereData, setPremiereData] = useState<any>(null);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [modalSubTitle, setModalSubTitle] = useState<string>("");
  const [modalFooter, setModalFooter] = useState<React.ReactNode>(null);
  const [createSearchQuery, setCreateSearchQuery] = useState<string>("");
  const [premiereNextDisabled, setPremiereNextDisabled] = useState(true);
  const [premiereRpcPending, setPremiereRpcPending] = useState(false);
  const { navigateWithScroll } = useScrollToTop();

  const mediaDetailConfig = MediaDetailConfig(
    currentMediaDetailsForEdit?.asset_type
  );

  const navigateToMediaPage = () => {
    if (currentMediaDetailsForEdit?.media_ref?.rel === "premiere") {
      navigateWithScroll(`/media/premiere`);
      return;
    }
    if (currentMediaDetailsForEdit?.content_type === 2) {
      navigateWithScroll(`/drive/video`);
      return;
    }
    if (currentMediaDetailsForEdit?.asset_type) {
      if (
        currentMediaDetailsForEdit.asset_type === "slates" ||
        currentMediaDetailsForEdit.asset_type === "campaign"
      ) {
        navigateWithScroll(`/media/video`);
      } else {
        navigateWithScroll(`/media/${currentMediaDetailsForEdit.asset_type}`);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTtsNextDisabled(true);
    setTtsRpcPending(false);
    emptyAiVoices();
    emptyAiLanguages();
  };

  const handleCloseModal = () => {
    localStorage.removeItem("metaPresetId");
    updateIsClearFilter && updateIsClearFilter(true);
    updateIsFilter && updateIsFilter(false);
    navigateToMediaPage();
    emptyAssetDetails();
    emptyMediaDetails();
    setIsModalOpenUpload(false);
    setPremiereData(null);
  };

  const handleNextClick = () => {
    const event = new CustomEvent("nextClick");
    window.dispatchEvent(event);
  };

  useEffect(() => {
    emptyAssetDetails();
    emptyMediaDetails();
  }, []);

  const openCreateOptionsModal = () => {
    if (isReadOnlyAccess) return;
    const defaultTeamId = (defaultTeam as any)?.id;
    if (defaultTeamId) {
      getUserBucketList({ team: defaultTeamId, status: 1 });
      getBunchListData({ keys: ["team"] });
    }
    setIsModalOpen(true);
    setModalContent(null);
    setModalSubTitle("");
    setModalFooter(null);
    setCreateSearchQuery("");
    setTtsNextDisabled(true);
    setTtsRpcPending(false);
  };

  const handleBackToOptions = () => {
    setModalContent(null);
    setModalSubTitle("");
    setModalFooter(null);
  };

  const backToOptionsButton = modalContent ? (
    <Button type="outline-primary" onClick={handleBackToOptions}>
      Back
    </Button>
  ) : null;

  const createSearchHeader = !modalContent ? (
    <div
      className="position-relative"
      style={{ width: "220px", marginRight: "10px" }}
    >
      <Search
        size={14}
        strokeWidth={2}
        style={{
          position: "absolute",
          top: "50%",
          left: "12px",
          transform: "translateY(-50%)",
          color: "#495057",
          pointerEvents: "none",
        }}
      />
      <input
        type="text"
        placeholder="Search"
        value={createSearchQuery}
        onChange={(e) => setCreateSearchQuery(e.target.value)}
        className="create-search-input-header"
        style={{
          width: "100%",
          height: "32px",
          padding: "0 24px 0 32px",
          fontSize: "13px",
          border: "1px solid #ced4da",
          borderRadius: "999px",
          background: "#ffffff",
          outline: "none",
          color: "#212529",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          transition: "background 0.15s ease, border-color 0.15s ease",
        }}
      />
      {createSearchQuery && (
        <button
          type="button"
          onClick={() => setCreateSearchQuery("")}
          aria-label="Clear search"
          style={{
            position: "absolute",
            top: "50%",
            right: "6px",
            transform: "translateY(-50%)",
            border: "none",
            background: "transparent",
            color: "#868e96",
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: 1,
            padding: "0 4px",
          }}
        >
          ×
        </button>
      )}
      <style>
        {`.create-search-input-header::placeholder { color: #adb5bd; }
          .create-search-input-header:hover { background: #e9ecef !important; }
          .create-search-input-header:focus { background: #fff !important; border-color: #ced4da !important; }`}
      </style>
    </div>
  ) : null;

  const updateModalContent = useCallback(
    (content: React.ReactNode, title: string, footer?: React.ReactNode) => {
      setModalContent(content);
      setModalSubTitle(title);
      setModalFooter(footer);
    },
    []
  );

  const updateModalFooter = useCallback((footer: React.ReactNode) => {
    setModalFooter(footer);
  }, []);

  const handleBulkUploadsStarted = (items: BulkMediaItem[]) => {
    // A second bulk-upload batch started while the wizard is already open
    // (first batch's tabs still there, possibly still uploading) must be
    // merged onto the existing tabs, not replace them - overwriting here
    // silently dropped the earlier tabs from bulkMediaItems, so closing what
    // looked like "one tab of several" actually emptied the whole list and
    // closed the entire wizard (handleRemoveBulkTab's empty-list check).
    setBulkMediaItems((prev) => {
      if (!isBulkWizardOpen || prev.length === 0) return items;
      const existingIds = new Set(prev.map((p) => p.id));
      return [...prev, ...items.filter((it) => !existingIds.has(it.id))];
    });
    setBulkActiveView("edit");
    if (items.length > 0) {
      // Focus the newly added batch's first item - either the wizard's
      // starting tab (nothing open yet) or appended after the existing tabs.
      const newActiveTab = isBulkWizardOpen ? bulkMediaItems.length : 0;
      setActiveBulkTab(newActiveTab);
      emptyMediaDetails();
      emptyAssetDetails();
      getMedia({ id: items[0].id });
    }
    setIsBulkWizardOpen(true);
    updateModal(true);
  };

  const performBulkTabSwitch = (index: number) => {
    setActiveBulkTab(index);
    // preserve current view: switching tabs on edit → edit, on publish → publish
    emptyMediaDetails();
    emptyAssetDetails();
    getMedia({ id: bulkMediaItems[index].id });
    // Clear the dirty flag so the freshly-switched-to tab starts clean instead
    // of inheriting a stale `true` left over from the tab/view just departed -
    // its own Details/Publish views will re-mark it if they actually have
    // unsaved work of their own.
    store.dispatch(setIsEditPageChanged(false));
  };

  // Entry point for the tab bar's onClick - guards against silently
  // discarding unsaved edits on the currently active tab.
  const handleBulkTabSwitch = (index: number) => {
    if (index === activeBulkTab) return;
    if (isEditPageChanged) {
      setPendingBulkTabAction(() => () => performBulkTabSwitch(index));
      setShowBulkTabAlert(true);
      return;
    }
    performBulkTabSwitch(index);
  };

  const handleCloseBulkWizard = () => {
    setIsBulkWizardOpen(false);
    updateModal(false);
    setBulkMediaItems([]);
    setActiveBulkTab(0);
    setBulkActiveView("edit");
    emptyMediaDetails();
    emptyAssetDetails();
    updateIsClearFilter && updateIsClearFilter(true);
    updateIsFilter && updateIsFilter(false);
    navigateWithScroll("/media/video");
    store.dispatch(setIsEditPageChanged(false));
  };

  const handleRemoveBulkTab = (index: number) => {
    const next = bulkMediaItems.filter((_, i) => i !== index);
    if (next.length === 0) {
      handleCloseBulkWizard();
      return;
    }
    // Keep active tab in bounds; if we closed the active tab move to the one before it
    const newActive =
      activeBulkTab >= next.length ? next.length - 1 : activeBulkTab;
    setBulkMediaItems(next);
    setActiveBulkTab(newActive);
    emptyMediaDetails();
    emptyAssetDetails();
    getMedia({ id: next[newActive].id });
    store.dispatch(setIsEditPageChanged(false));
  };

  // Entry point for the tab bar's close (X) button - guards against silently
  // discarding unsaved edits on the tab being closed.
  const handleCloseBulkTabClick = (index: number) => {
    if (index === activeBulkTab && isEditPageChanged) {
      setPendingBulkTabAction(() => () => handleRemoveBulkTab(index));
      setShowBulkTabAlert(true);
      return;
    }
    handleRemoveBulkTab(index);
  };

  const handleDone = () => {
    localStorage.removeItem("socialPresetId");
    const event = new CustomEvent("doneClick");
    window.dispatchEvent(event);
    if (isBulkWizardOpen) {
      // Tab close + next tab's media.get is handled by MediaPublish's
      // "doneClick" listener via its closeHandler (-> handleRemoveBulkTab),
      // so don't call it again here - doing so used to fire a duplicate
      // media.get and could leave the new tab showing the closed tab's data.
      setBulkActiveView("edit");
    } else {
      navigateToMediaPage();
      setPublishModal(false);
    }
  };

  useEffect(() => {
    const handleBackClick = (event: CustomEvent) => {
      const id = event.detail?.id;
      getMedia({ id: id || currentMediaDetailsForEdit?.id });
      if (isBulkWizardOpen) {
        setBulkActiveView("edit");
      } else {
        setPublishModal(false);
        setIsModalOpenUpload(true);
      }
    };
    window.addEventListener("backClick", handleBackClick as EventListener);
    return () => {
      window.removeEventListener("backClick", handleBackClick as EventListener);
    };
  }, [isBulkWizardOpen]);

  const handleBackClickEvent = () => {
    const event = new CustomEvent("backClick", {
      detail: { id: currentMediaDetailsForEdit?.id },
    });
    window.dispatchEvent(event);
  };

  const handlePremiereReady = (data: any) => {
    setPremiereData(data);

    if (data?.posterFile && data?.mediaRegisterResp && data?.posterUploadCb) {
      const resp = data.mediaRegisterResp;
      const mediaId = resp.id;
      const posterUploadCb = data.posterUploadCb;

      callRegisterPosterAssetOnCms(
        {
          file: data.posterFile,
          mediaId,
          cdn: (resp.cdn ?? []).map((c: { id: string }) => ({
            id: c.id,
          })),
          damid: resp.damid,
          business: resp.business,
        },
        (assetResp: { status: number; data?: { jwt?: string } }) => {
          if (assetResp.status === 200) {
            posterUploadCb(
              { entity: mediaId, key: "thumb" },
              assetResp.data?.jwt
            );
          }
        }
      );
    }
  };

  const isReadOnlyAccess = (() => {
    if (!currentMediaDetailsForEdit?.team || !bunchProductList?.length) {
      return false;
    }
    const currentTeam = bunchProductList.find(
      (team: any) => team.id === currentMediaDetailsForEdit.team
    );
    return currentTeam?.access === "read";
  })();

  return (
    <div className="upload-btn">
      <span
        className="btn btn-outline-primary"
        onClick={openCreateOptionsModal}
        style={{
          cursor: isReadOnlyAccess ? "not-allowed" : "pointer",
          opacity: isReadOnlyAccess ? 0.5 : 1,
        }}
      >
        <Video size={24} strokeWidth={1.2} />
        Create
      </span>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          title={modalContent ? modalSubTitle : "CREATE"}
          onClose={closeModal}
          width={modalSubTitle === "CREATE > Text to Speech" ? "100%" : "820px"}
          height={
            modalSubTitle === "CREATE > Text to Speech"
              ? "100vh"
              : !modalContent
                ? "560px"
                : "auto"
          }
          bgColor={!modalContent ? "bgGrey" : ""}
          closeOnOutsideClick={!modalContent ? true : false}
          hideCloseButton={!modalContent}
          closeOnEsc={!modalContent}
          headerRight={createSearchHeader}
          footer={
            modalSubTitle === "CREATE > Premiere" ? (
              <div
                className="d-flex justify-content-between w-100">
                {backToOptionsButton}
                <Button
                  type="primary"
                  isDisabled={premiereNextDisabled || premiereRpcPending}
                  onClick={() => {
                    const event = new CustomEvent("nextClickPremiere");
                    window.dispatchEvent(event);
                  }}
                >
                  {premiereRpcPending ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Loading...
                    </>
                  ) : (
                    "Next"
                  )}
                </Button>
              </div>
            ) : modalSubTitle === "CREATE > Text to Speech" ? (
              <div
                className="d-flex justify-content-between w-100">
                {backToOptionsButton}
                <Button
                  type="primary"
                  isDisabled={ttsNextDisabled || ttsRpcPending}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("customNextClick"));
                  }}
                >
                  {ttsRpcPending ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Loading...
                    </>
                  ) : (
                    "SAVE & NEXT"
                  )}
                </Button>
              </div>
            ) : (
              <div
                className="d-flex justify-content-between w-100"
                style={{ flexDirection: "row-reverse" }}
              >
                {modalFooter}
                {backToOptionsButton}
              </div>
            )
          }
        >
          {modalContent || (
            <CreateOptions
              closeModal={closeModal}
              updateModalContent={updateModalContent}
              updateModalFooter={updateModalFooter}
              onPremiereNextDisabledChange={setPremiereNextDisabled}
              onPremiereRpcPendingChange={setPremiereRpcPending}
              onTtsNextDisabledChange={setTtsNextDisabled}
              onTtsRpcPendingChange={setTtsRpcPending}
              openUpload={() => {
                setIsModalOpenUpload(true);
                setIsModalOpen(false);
              }}
              openPremiereEditor={handlePremiereReady}
              externalSearchQuery={createSearchQuery}
              onBulkUploadsStarted={handleBulkUploadsStarted}
            />
          )}
        </Modal>
      )}
      {isModalOpenUpload && (
        <Modal
          isOpen={isModalOpenUpload}
          onClose={handleCloseModal}
          title={
            premiereData
              ? "PREMIERE > Metadata"
              : currentMediaDetailsForEdit?.source === "embed"
                ? "Embedded Video"
                : `Upload ${
                    currentMediaDetailsForEdit?.asset_type === "audio" ||
                    currentMediaDetailsForEdit?.asset_type === "podcast"
                      ? "Audio"
                      : "Video"
                  }`
          }
          height="100vh"
          width="100%"
          closeOnOutsideClick={false}
          footer={
            <Button onClick={handleNextClick}>
              {mediaDetailConfig.social_publish_page.enabled &&
              currentMediaDetailsForEdit?.media_ref?.rel !== "premiere"
                ? "Save & Next"
                : "SAVE"}
            </Button>
          }
        >
          <MediaDetails
            isWizard={true}
            premiereData={premiereData}
            closeHandler={() => {
              updateIsClearFilter && updateIsClearFilter(true);
              updateIsFilter && updateIsFilter(false);
              localStorage.removeItem("metaPresetId");
              navigateToMediaPage();
              setIsModalOpenUpload(false);
              setPremiereData(null);
              emptyMediaDetails();
              emptyAssetDetails();
            }}
            closeModal={() => {
              localStorage.removeItem("metaPresetId");
              updateIsClearFilter && updateIsClearFilter(true);
              updateIsFilter && updateIsFilter(false);
              setIsModalOpenUpload(false);
              setPremiereData(null);
              emptyMediaDetails();
              emptyAssetDetails();
            }}
            openPublish={() => {
              localStorage.removeItem("metaPresetId");
              setIsModalOpen(false);
              setIsModalOpenUpload(false);
              setPublishModal(true);
            }}
          />
        </Modal>
      )}
      {isBulkWizardOpen && (
        <Modal
          isOpen={isBulkWizardOpen}
          onClose={handleCloseBulkWizard}
          title="Bulk Upload"
          height="100vh"
          width="100%"
          closeOnOutsideClick={false}
          footer={
            bulkActiveView === "publish" ? (
              <div className="d-flex justify-content-between w-100">
                <Button
                  type="outline-primary"
                  onClick={() => setBulkActiveView("edit")}
                >
                  Back
                </Button>
                <Button onClick={handleDone}>Done</Button>
              </div>
            ) : (
              <Button onClick={handleNextClick}>Save &amp; Next</Button>
            )
          }
        >
          {/* Chrome-style tab strip */}
          <style>{`
            .bulk-tab {
              position: relative;
              display: inline-flex;
              align-items: center;
              padding: 8px 18px 9px;
              font-size: 12px;
              font-weight: 400;
              color: #5f6368;
              background: transparent;
              border: none;
              cursor: pointer;
              white-space: nowrap;
              max-width: 180px;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 0;
              transition: background 0.12s ease, color 0.12s ease;
              z-index: 0;
              border-radius: 10px 10px 0 0;
            }
            .bulk-tab:hover:not(.bulk-tab--active) {
              background: #e2e5e9;
              color: #202124;
            }
            .bulk-tab--active {
              font-weight: 500;
              color: #202124;
              background: #fff;
              margin-bottom: -2px;
              padding-bottom: 11px;
              z-index: 2;
              overflow: visible;
              border-bottom: 2px solid #fff;
            }
            .bulk-tab--active::before {
              content: '';
              position: absolute;
              bottom: -1px;
              left: -10px;
              width: 10px;
              height: 11px;
              background: transparent;
              border-bottom-right-radius: 10px;
              box-shadow: 4px 2px 0 0 #fff;
              pointer-events: none;
              z-index: 2;
            }
            .bulk-tab--active::after {
              content: '';
              position: absolute;
              bottom: -1px;
              right: -10px;
              width: 10px;
              height: 11px;
              background: transparent;
              border-bottom-left-radius: 10px;
              box-shadow: -4px 2px 0 0 #fff;
              pointer-events: none;
              z-index: 2;
            }
          `}</style>
          <div
            style={{
              position: "sticky",
              top: "-20px",
              zIndex: 3,
              background: "#e8eaed",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 2,
                padding: "8px 12px 0",
                background: "#e8eaed",
                borderBottom: "1px solid #c6cacd",
                overflowX: "auto",
              }}
            >
              {bulkMediaItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => handleBulkTabSwitch(index)}
                  title={item.name}
                  className={`bulk-tab${activeBulkTab === index ? " bulk-tab--active" : ""}`}
                >
                  <span
                    style={{
                      display: "block",
                      maxWidth: 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name.split(".")[0]}
                  </span>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseBulkTabClick(index);
                    }}
                    title="Close"
                    style={{
                      marginLeft: 6,
                      lineHeight: 0,
                      borderRadius: "50%",
                      padding: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      opacity: 0.6,
                      flexShrink: 0,
                    }}
                  >
                    <X size={11} />
                  </span>
                </button>
              ))}
            </div>
          </div>
          {/* Edit or Publish view */}
          <div style={{ flex: 1, overflow: "auto" }}>
            {bulkActiveView === "edit" ? (
              <MediaDetails
                isWizard={true}
                closeHandler={handleCloseBulkWizard}
                closeModal={handleCloseBulkWizard}
                openPublish={() => {
                  localStorage.removeItem("metaPresetId");
                  setBulkActiveView("publish");
                }}
              />
            ) : (
              <MediaPublish
                isWizard={true}
                closeHandler={() => handleRemoveBulkTab(activeBulkTab)}
              />
            )}
          </div>
        </Modal>
      )}
      {publishModal && (
        <Modal
          isOpen={publishModal}
          title="Social Publish"
          onClose={() => {
            updateIsClearFilter && updateIsClearFilter(true);
            updateIsFilter && updateIsFilter(false);
            localStorage.removeItem("socialPresetId");
            navigateToMediaPage();
            emptyAssetDetails();
            emptyMediaDetails();
            setPublishModal(false);
          }}
          height="100vh"
          width="100%"
          closeOnOutsideClick={false}
          footer={
            <div className="d-flex justify-content-between w-100">
              <Button type="outline-primary" onClick={handleBackClickEvent}>
                Back
              </Button>
              <Button onClick={handleDone}>Done</Button>
            </div>
          }
        >
          <MediaPublish
            isWizard={true}
            closeHandler={() => {
              updateIsClearFilter && updateIsClearFilter(true);
              updateIsFilter && updateIsFilter(false);
              localStorage.removeItem("socialPresetId");
              navigateToMediaPage();
              setPublishModal(false);
              emptyMediaDetails();
              emptyAssetDetails();
            }}
          />
        </Modal>
      )}
      <AlertDialog
        isOpen={showBulkTabAlert}
        onClose={() => {
          setShowBulkTabAlert(false);
          setPendingBulkTabAction(null);
        }}
        onConfirm={() => {
          pendingBulkTabAction?.();
          setPendingBulkTabAction(null);
        }}
        title="Unsaved changes"
        message="You have unsaved changes on this tab. Switching now will discard them. Continue?"
        confirmText="Discard & Continue"
        cancelText="Stay"
        confirmButtonType="danger"
      />
    </div>
  );
};

const mapState = (state: RootState) => ({
  createBucketOrder: state.users.createBucketOrder,
  defaultTeam: state.constants.defaultTeam,
  adminBucketList: state.media.adminBucketList.list,
  isEditPageChanged: state.media.isEditPageChanged,
});
const mapDispatch = {
  callRegisterPosterAssetOnCms,
  getUserBucketList,
  getBunchListData,
  emptyAiVoices,
  emptyAiLanguages,
  updateModal,
};
const connector = connect(mapState, mapDispatch);

export const FileUploader = connector(FileUploaderBase);
